import { DataConnectionState } from "./constants"

/**
 * Minimal asset shape required for data connection state derivation.
 */
export interface DataConnectionAsset {
  id?: string
  name?: string
  monitoring_status?: string | null
  monitoring_start?: string | null
  isParent?: boolean | null
  parentId?: string | null
  class?: string | null
  kind?: string | null
  status?: string | null
  children?: Array<DataConnectionAsset> | null
  systemId?: string | null
  systemName?: string | null
  activeAssignment?: {
    system?: { id?: string | null; name?: string | null } | string | null
    systemName?: string | null
    installation_override?: string | null
    status?: string | null
    active?: boolean | null
    monitored?: boolean | null
  } | null
  assignments?: {
    all?: Array<{
      id?: string | null
      active?: boolean | null
      status?: string | null
      monitored?: boolean | null
      system?: { id?: string | null; name?: string | null } | string | null
      systemName?: string | null
    }> | null
    latest?: string | null
    active?: string[] | null
  } | null
  current_level_updated_at?: string | null
  latest_activity?: any
  power?: any
  energy?: any
  state_of_charge?: any
}

/**
 * System info derived from an assigned asset.
 */
export interface AssignedSystemInfo {
  id: string
  name?: string | null
}

/**
 * Resolves the assigned system ID and optional name for an asset.
 */
export function getAssetAssignedSystem(
  asset?: DataConnectionAsset | null,
  systemIdOverride?: string | null,
  systemNameOverride?: string | null,
): AssignedSystemInfo | null {
  if (systemIdOverride) {
    return { id: systemIdOverride, name: systemNameOverride ?? null }
  }
  if (!asset) return null

  // 1. Direct systemId property
  if (asset.systemId) {
    return { id: asset.systemId, name: asset.systemName ?? null }
  }

  // 2. activeAssignment
  const active = asset.activeAssignment
  if (active?.system) {
    if (typeof active.system === "string") {
      return { id: active.system, name: active.systemName ?? null }
    }
    if (typeof active.system === "object" && active.system.id) {
      return {
        id: active.system.id,
        name: active.system.name ?? active.systemName ?? null,
      }
    }
  }

  // 3. assignments.all
  const assignments = asset.assignments
  if (assignments?.all && Array.isArray(assignments.all)) {
    const activeIds = new Set(assignments.active || [])
    const activeAssign =
      assignments.all.find(
        (a) =>
          (activeIds.has(a.id || "") || a.id === assignments.latest || Boolean(a.active)) &&
          Boolean(a.system),
      ) || assignments.all.find((a) => Boolean(a.system))

    if (activeAssign?.system) {
      if (typeof activeAssign.system === "string") {
        return { id: activeAssign.system, name: activeAssign.systemName ?? null }
      }
      if (typeof activeAssign.system === "object" && activeAssign.system.id) {
        return {
          id: activeAssign.system.id,
          name: activeAssign.system.name ?? activeAssign.systemName ?? null,
        }
      }
    }
  }

  return null
}

/**
 * Domain-level connection ownership.
 * A connection is owned by the enclosing System (precedence), the Asset itself, or a Child Module.
 */
export type ConnectionOwnership = "system" | "asset" | "module" | null

/**
 * Parameters for formatting connection tooltips and accessibility labels.
 */
export interface FormatConnectionTooltipParams {
  state: DataConnectionState
  ownership?: ConnectionOwnership
  relativeTimeLong?: string | null
  relativeTimeShort?: string | null
}

export interface ConnectionTooltipResult {
  tooltipTitle: string
  labelText: string
  ariaLabel: string
}

/**
 * Pure domain formatter for connection tooltip and screen reader labels.
 */
export function formatConnectionTooltip({
  state,
  ownership,
  relativeTimeLong,
  relativeTimeShort,
}: FormatConnectionTooltipParams): ConnectionTooltipResult {
  const ownerSuffix =
    ownership === "system"
      ? "owned by system"
      : ownership === "asset"
        ? "owned by asset"
        : ownership === "module"
          ? "owned by module"
          : null

  if (state === DataConnectionState.Active) {
    const titleBase = ownerSuffix
      ? `Data connection active (${ownerSuffix})`
      : "Data connection active"
    const tooltipTitle = relativeTimeLong
      ? `${titleBase} - last data ${relativeTimeLong}`
      : titleBase
    return {
      tooltipTitle,
      labelText: "Data active",
      ariaLabel: tooltipTitle,
    }
  }

  if (state === DataConnectionState.Inactive) {
    const titleBase = ownerSuffix
      ? `Data connection inactive (${ownerSuffix})`
      : "Data connection inactive"
    const tooltipTitle = relativeTimeLong
      ? `${titleBase} - last data ${relativeTimeLong}`
      : titleBase
    return {
      tooltipTitle,
      labelText: `Data inactive${relativeTimeShort ? ` · ${relativeTimeShort}` : ""}`,
      ariaLabel: tooltipTitle,
    }
  }

  // Not configured
  const tooltipTitle = ownerSuffix
    ? `Not configured (${ownerSuffix}) - set up data connection`
    : "Not configured - set up data connection"
  return {
    tooltipTitle,
    labelText: "Set up data connection",
    ariaLabel: tooltipTitle,
  }
}

/**
 * Extracts the latest activity timestamp among active children of a hybrid/parent asset.
 */
export function getLatestChildActivityTimestamp(
  children?: Array<DataConnectionAsset> | null
): string | null {
  if (!children || children.length === 0) return null

  const activeChildTimestamps = children
    .filter((c) => isChildModuleActive(c))
    .map(
      (c: any) =>
        c.current_level_updated_at ??
        c.latest_activity?.latestEvent?.createdAt ??
        c.monitoring_start
    )
    .filter(Boolean) as string[]

  if (activeChildTimestamps.length > 0) {
    activeChildTimestamps.sort(
      (a: string, b: string) => new Date(b).getTime() - new Date(a).getTime()
    )
    return activeChildTimestamps[0]
  }

  return null
}

/**
 * Connection item shape returned from getAssetDataConnections query.
 */
export interface DataConnectionRecord {
  id?: string
  status?: string | null
  connectorType?: string | null
  connectorName?: string | null
  externalId?: string | null
}

/**
 * Checks whether an asset is a hybrid or parent asset whose data management lives on child modules.
 */
export function isHybridOrParentAsset(asset?: DataConnectionAsset | null): boolean {
  if (!asset) return false
  return Boolean(
    asset.isParent ||
      asset.class?.toLowerCase() === "hybrid" ||
      asset.kind?.toLowerCase() === "hybrid" ||
      (asset.children && asset.children.length > 0)
  )
}

/**
 * Checks whether an individual child module of a hybrid asset has active monitoring / connection.
 */
export function isChildModuleActive(child?: DataConnectionAsset | null): boolean {
  if (!child) return false

  // 1. Direct monitoring status
  const monStatus = child.monitoring_status?.toLowerCase()
  if (monStatus === "active") return true

  // 2. Active assignments
  if (
    child.assignments?.all?.some(
      (a) => Boolean(a?.active) && (a?.status?.toLowerCase() === "active" || Boolean(a?.monitored))
    )
  ) {
    return true
  }
  if (
    child.activeAssignment &&
    (child.activeAssignment.status?.toLowerCase() === "active" || Boolean(child.activeAssignment.active))
  ) {
    return true
  }

  // 3. Raw status
  const rawStatus = child.status?.toLowerCase()
  if (rawStatus === "active") return true

  // 4. Standalone data connection resolution as child asset
  const standalone = resolveDataConnectionState({
    ...child,
    isParent: false,
    children: null,
  })
  if (standalone === DataConnectionState.Active) {
    return true
  }

  // 5. Active telemetry presence
  if (
    monStatus !== "inactive" &&
    Boolean(
      child.power ||
        child.energy ||
        child.state_of_charge ||
        child.current_level_updated_at ||
        child.latest_activity?.latestEvent?.createdAt
    )
  ) {
    return true
  }

  return false
}

/**
 * Derives the data connection state from queried connection records if available.
 *
 * Rules:
 * 1. If connection records exist and the list is empty:
 *    The asset has no data connector configured -> NotConfigured.
 * 2. If at least one connection has status 'active':
 *    The connector is set up and active -> Active.
 * 3. If connections exist but none are active:
 *    The connector is configured but inactive -> Inactive.
 */
export function resolveStateFromConnections(
  connections: DataConnectionRecord[] | null | undefined,
  fallbackState: DataConnectionState
): DataConnectionState {
  if (!connections) return fallbackState

  if (connections.length === 0) {
    return DataConnectionState.NotConfigured
  }

  const hasActive = connections.some(
    (c) => c.status?.toLowerCase() === "active"
  )

  return hasActive ? DataConnectionState.Active : DataConnectionState.Inactive
}

/**
 * Derives the 3-state data connection status for an asset from entity attributes:
 *
 * Rules:
 * 1. Hybrid assets: Parent is Active if at least one child module is active; otherwise Inactive.
 * 2. Direct monitoring status: "active" -> Active.
 * 3. Parent assets without children loaded default to Inactive.
 * 4. Inactive vs Not Configured:
 *    - In backend SQL views, monitoring_status defaults to 'inactive' when COUNT(*) = 0.
 *    - An asset is genuinely Inactive if it has connection/telemetry evidence (monitoring_start,
 *      telemetry timestamp, latest activity, or explicit hasConnection=true).
 *    - If an asset has no connection evidence, it is NotConfigured.
 */
export function resolveDataConnectionState(
  asset?: DataConnectionAsset | null,
  hasConnection?: boolean | null,
  lastDataTimestamp?: string | Date | null
): DataConnectionState {
  if (!asset) return DataConnectionState.NotConfigured

  // If explicit hasConnection flag is false, it's unconfigured
  if (hasConnection === false) {
    return DataConnectionState.NotConfigured
  }

  const isHybrid = isHybridOrParentAsset(asset)

  // For hybrid assets -> parent MUST show active if at least one child is active; otherwise, show inactive
  if (isHybrid && asset.children && asset.children.length > 0) {
    const hasActiveChild = asset.children.some((c) => isChildModuleActive(c))
    return hasActiveChild ? DataConnectionState.Active : DataConnectionState.Inactive
  }

  // Check direct monitoring status
  const rawStatus = asset.monitoring_status?.toLowerCase()
  if (rawStatus === "active") {
    return DataConnectionState.Active
  }

  // Parent assets without children loaded default to Inactive
  const isParent = Boolean(asset.isParent || isHybrid)
  if (isParent) {
    return DataConnectionState.Inactive
  }

  // If explicit hasConnection flag is true, it is Inactive (since it was not active above)
  if (hasConnection === true) {
    return DataConnectionState.Inactive
  }

  // Check whether the asset has any evidence of a configured connection or telemetry data.
  // In the backend, asset_list_view defaults monitoring_status to 'inactive' when COUNT(*) is 0.
  // Therefore, an unconfigured asset also has rawStatus === "inactive".
  // A truly inactive connection has telemetry history or a monitoring assignment start.
  const hasTelemetryOrAssignment = Boolean(
    asset.monitoring_start ||
    asset.current_level_updated_at ||
    asset.latest_activity?.latestEvent?.createdAt ||
    asset.latest_activity?.start ||
    (asset as any).power ||
    (asset as any).energy ||
    lastDataTimestamp
  )

  if (rawStatus === "inactive" && hasTelemetryOrAssignment) {
    return DataConnectionState.Inactive
  }

  // If there is no connection evidence, treat as not configured
  return DataConnectionState.NotConfigured
}

/** Formats a timestamp into a relative time string. */
export function formatConnectionRelativeTime(
  timestamp?: string | Date | null,
  compact = false,
): string | null {
  if (!timestamp) return null
  const date = typeof timestamp === "string" ? new Date(timestamp) : timestamp
  const timeMs = date.getTime()
  if (Number.isNaN(timeMs)) return null

  const diffMs = Math.max(0, Date.now() - timeMs)
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (compact) {
    if (diffMins < 1) return "now"
    if (diffMins < 60) return `${diffMins}m`
    if (diffHours < 24) return `${diffHours}h`
    return `${diffDays}d`
  }

  if (diffMins < 1) return "just now"
  if (diffMins === 1) return "1 minute ago"
  if (diffMins < 60) return `${diffMins} minutes ago`
  if (diffHours === 1) return "1 hour ago"
  if (diffHours < 24) return `${diffHours} hours ago`
  if (diffDays === 1) return "1 day ago"
  return `${diffDays} days ago`
}
